import {
  AssetCard,
  Heading,
  Markdown,
  RollingAmount,
  StatsBox,
  Typography,
} from '@altify/components';
import { useNavigate } from '@tanstack/react-router';
import portfolioMessages from 'app/_authenticated/portfolio/messages';
import verificationMessages from 'app/_authenticated/verification/messages';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';
import GainLoss from 'components/GainLoss/GainLoss';
import { InfoButton } from 'components/InfoButton/InfoButton';
import ProductGraph from 'components/ProductGraph/ProductGraph2';
import { useProductGraph } from 'hooks/products/useGraphs';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
  useProductWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import { useVerification } from 'hooks/useVerification';
import { useEffect, useRef, useState } from 'react';
import { useCancelCryptoDepositMutation } from 'store/slices/deposits-api';
import { useGetProductDetailsQuery } from 'store/slices/products-api';
import { getMarketingUrl } from 'utils/app-urls';
import { formatCurrency, getCurrencyTypeLabel } from 'utils/currencies';
import productMessages from '../messages';
import ProductButtons from './ProductButtons';
import {
  DisclaimerModal,
  ReturnsModal,
  VerifyModal,
} from './ProductModals/ProductModals';
import { getDisplayAssetCode, Language } from '@altify/utils';
import Tabs from 'components/Tabs/Tabs';
import { TimeFrameKey, timeFrames } from 'data/time-frames';
import { useCryptoDepositProduct } from 'hooks/useCryptoDepositProduct';
import useAppLanguage from 'hooks/useAppLanguage';
import { ActiveProductDtoTypeEnum } from 'services/generated';
import { CustomTabs } from 'components/CustomTabs/CustomTabs';
import ConstituentsPie from './ProductBundle/PieChart/ConstituentsPie';
import downloadIcon from 'assets/icons/download.svg';

function ProductBundleData({
  code,
  timeFrame,
  setTimeframeKey,
  methodology,
  factsheet,
  activeBundle,
}: {
  code: string;
  timeFrame: TimeFrameKey;
  setTimeframeKey: (timeFrame: TimeFrameKey) => void;
  methodology: string;
  factsheet: string;
  activeBundle: any;
}) {
  const navigate = useNavigate();
  const { product } = useProductWithHoldingsAndRates(code);
  const { data: productDetails } = useGetProductDetailsQuery(code);
  const { functionalCurrency } = useFunctionalCurrency();
  const language = useAppLanguage();
  const [cancelCryptoDepositOrder] = useCancelCryptoDepositMutation();
  const cryptoDepositProduct = useCryptoDepositProduct(product?.code);

  const { isVerified, isVerificationPending, isVerificationInProgress } =
    useVerification();
  const { products } = useProductsWithHoldingsAndRates();

  const undelyingProductCode = product?.redeemableTo ?? code;

  const { graph } = useProductGraph(undelyingProductCode, timeFrame);
  const hasHoldings = product?.totalAmount! > 0;

  const graphData =
    graph == null
      ? []
      : graph.graph.map(point => ({
          value: point.value,
          change: point.relativeChange * 100,
          x: point.timestamp.getTime() / 1000,
          y: point.normalizedValue * 100,
        }));

  const assetGainPercentage = graphData?.[graphData.length - 1]?.change;

  // TODO: This should probably be moved to a variant of the useProduct hook
  const percentageOfPortfolio = products?.find(
    product => product.code === code,
  )?.breakdownPercentage;

  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);
  const [returnsModalOpen, setReturnsModalOpen] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [pendingDepositModalOpen, setPendingDepositModalOpen] = useState(false);

  // Derived state:
  const messages = productMessages[language].productData;
  const verificationPendingMessages = verificationMessages[language];
  const overTimePeriodMessages = portfolioMessages[language].overTimePeriod;

  const holdingsItems = [
    {
      label: (
        <div className="flex flex-row items-center gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.gainLoss}
          </Typography>
          <InfoButton
            title={messages.gainLoss}
            text={messages.holdingsGainLossTooltip}
            useDesktopModal={true}
          />
        </div>
      ),
      value: (
        <>
          <div className="flex flex-row justify-end gap-2">
            <GainLoss
              symbol={functionalCurrency?.symbol!}
              value={product?.unrealizedGainLossAmount ?? 0}
              className="flex items-center whitespace-nowrap font-normal tracking-widest text-base"
            />
            <GainLoss
              arrow={false}
              symbol={''}
              value={product?.unrealizedGainLossPercentage ?? 0}
              className="-my-0.5 font-normal tracking-widest text-base"
              background={true}
            />
          </div>
        </>
      ),
    },
    {
      label: (
        <div className="flex flex-row items-center  gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.value}
          </Typography>
          <InfoButton
            title={messages.value}
            text={messages.valueTooltip}
            useDesktopModal={true}
          />
        </div>
      ),
      value: (
        <Typography size="medium" className="text-white text-base">
          {formatCurrency(
            product?.totalAmountInDisplay,
            functionalCurrency?.code,
          )}
        </Typography>
      ),
    },
    {
      label: (
        <div className="flex flex-row items-center gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.averageCostPrice}
          </Typography>
          <InfoButton
            title={messages.averageCostPrice}
            text={messages.averageCostTooltip}
            useDesktopModal={true}
          />
        </div>
      ),
      value: (
        <Typography size="medium" className="text-white text-base">
          {formatCurrency(product?.wac, functionalCurrency?.code!)}
        </Typography>
      ),
    },
    {
      label: (
        <div className="flex flex-row items-center gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.balance}
          </Typography>
        </div>
      ),
      value: (
        <Typography size="medium" className="text-white text-base">
          {formatCurrency(product?.totalAmount, product?.code)}
        </Typography>
      ),
    },
    {
      label: (
        <div className="flex flex-row items-center gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.portfolioShare}
          </Typography>
        </div>
      ),
      value: (
        <Typography size="medium" className="text-white text-base">
          {`${percentageOfPortfolio?.toFixed(2)}%`}
        </Typography>
      ),
    },
  ];

  const lockedHoldingsItems = [
    {
      label: (
        <div className="flex flex-row items-center gap-2">
          <Typography size="medium" className="text-neutral-300 text-base">
            {messages.balance}
          </Typography>
        </div>
      ),
      value: (
        <Typography size="medium" className="text-white text-base">
          {formatCurrency(product?.totalAmount, product?.code)}
        </Typography>
      ),
    },
  ];

  const mobileTabs = () => {
    const tabs = [];

    tabs.push({
      key: 'about',
      title: messages.about,
      content: () => (
        <>
          {/* MOBILE ABOUT CARD */}
          <Card className="flex-col bg-neutral-600 flex-shrink-0 order-0 rounded-xxl mt-4">
            {product?.type == ActiveProductDtoTypeEnum.Locked ? (
              <LockedProductContent />
            ) : (
              <ProductContent />
            )}
          </Card>

          <Card className={`flex flex-col bg-neutral-600 rounded-xxl mt-4`}>
            <div className="flex flex-col justify-center items-center lg:items-start">
              <Heading level={2} className="font-semibold leading-9 lg:flex text-center">
                {messages.breakdown}
              </Heading>
            </div>
            <ConstituentsPie noPadding bundle={activeBundle} />
          </Card>

          <MobileDocumentsAndRiskContent />
        </>
      ),
    });

    // Show tab sections if holdings, vault balance or vault reserved amount > 0
    if (hasHoldings && product?.type !== ActiveProductDtoTypeEnum.Locked) {
      tabs.unshift({
        key: 'holdings',
        title: messages.yourHoldings,
        content: () => (
          <>
            <>
              <AssetCard
                showAssetDetails={false}
                iconComponent={CurrencyIcon}
                iconProps={{
                  code: product?.code!,
                  size: 25,
                  style: { marginRight: 5 },
                }}
                showButton={false}
                assetName={product?.name}
                assetSubtitle=""
                items={holdingsItems}
                className={'w-full my-4 gap-2'}
              />
            </>
          </>
        ),
      });
    }

    if (hasHoldings && product?.type == ActiveProductDtoTypeEnum.Locked) {
      tabs.unshift({
        key: 'holdings',
        title: messages.yourHoldings,
        content: () => (
          <>
            <>
              <AssetCard
                showAssetDetails={false}
                iconComponent={CurrencyIcon}
                iconProps={{
                  code: product?.code!,
                  size: 25,
                  style: { marginRight: 5 },
                }}
                showButton={false}
                assetName={product?.name}
                assetSubtitle=""
                items={lockedHoldingsItems}
                className={'w-full my-4 gap-2'}
              />
            </>
          </>
        ),
      });
    }

    return tabs;
  };

  // #region INLINE CONTENT RENDERERS
  const ProductContent = () => {
    return (
      <>
        <div className="flex flex-col lg:hidden">
          <Heading
            level={2}
            className="font-semibold leading-9 hidden lg:flex my-2 ">
            {messages.about} {product?.name}
          </Heading>
        </div>
        {productDetails?.productCopy?.productDescription ? (
          <>
            <div className="flex flex-row gap-4 my-4">
              <StatsBox
                className="px-5 py-4 flex-col basis-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                key={'ticker'}>
                <Typography size="medium" className="text-white mb-0">
                  {getDisplayAssetCode(product?.code)}
                </Typography>
                <Typography size="small" className="text-grey">
                  {messages.ticker}
                </Typography>
              </StatsBox>

              <StatsBox
                className="px-5 py-4 flex-col basis-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                key={'rebalanceFrequency'}>
                <Typography size="medium" className="text-white mb-0">
                  {messages.monthly}
                </Typography>
                <Typography size="small" className="text-grey">
                  {messages.rebalanceFrequency}
                </Typography>
              </StatsBox>
            </div>
            
            <Markdown
              styles={{
                heading4: {
                  fontSize: 16,
                },
              }}>
              {productDetails?.productCopy?.productDescription}
            </Markdown>
          </>
        ) : (
          <>
            <div className="mt-4 flex h-10 flex-col items-start justify-start justify-between text-left text-body-text">
              <div className="skeleton h-10 w-1/4" />
            </div>
          </>
        )}
      </>
    );
  };

  const LockedProductContent = () => {
    return (
      <div className="md-container">
        {productDetails?.productCopy?.whoHeading ? (
          <Markdown
            styles={{
              heading4: {
                fontSize: 16,
              },
            }}>
            {productDetails?.productCopy?.whoHeading}
          </Markdown>
        ) : (
          <>
            <div className="mt-4 flex h-10 flex-col items-start justify-start justify-between text-left text-body-text">
              <div className="skeleton h-10 w-1/4" />
            </div>
          </>
        )}
      </div>
    );
  };

  const DesktopHoldings = () => {
    return (
      <>
        <div className={'rounded-2xl'}>
          {product?.type == ActiveProductDtoTypeEnum.Locked
            ? lockedHoldingsItems.map((item, index) => (
                <div className="flex flex-row my-4 justify-between" key={index}>
                  {item.label}
                  {item.value}
                </div>
              ))
            : holdingsItems.map((item, index) => (
                <div className="flex flex-row my-4 justify-between" key={index}>
                  {item.label}
                  {item.value}
                </div>
              ))}
        </div>
      </>
    );
  };

  const ButtonGroup = () => {
    return (
      <>
        <div className="fixed bottom-0 left-0 md:flex md:flex-col md:flex-grow md:justify-end z-30 w-full p-4 lg:static lg:mt-8 lg:p-0">
          {product && (
            <ProductButtons product={product} isVerified={isVerified} />
          )}
        </div>
        {(showNotVerifiedButton ||
          showNotVerificationPendingButton ||
          showDepositButton ||
          showPendingDepositButton) && (
          <div className="mt-8 flex items-center justify-start">
            {showNotVerifiedButton && (
              <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                <Button
                  type="button"
                  main
                  full
                  onClick={() => navigate({ to: '/verification' })}>
                  {messages.notVerified}
                </Button>
              </div>
            )}
            {showNotVerificationPendingButton && (
              <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                <Button
                  type="button"
                  main
                  full
                  isVerificationPending
                  onClick={() => setVerifyModal(true)}>
                  {messages.verifyPending}
                </Button>
              </div>
            )}
            {showDepositButton && (
              <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                <Button
                  main
                  full
                  type="button"
                  onClick={() =>
                    navigate({
                      to: '/funds/deposit/crypto/$productCode',
                      params: { productCode: code },
                    })
                  }>
                  {messages.depositBtn}
                </Button>
              </div>
            )}
            {showPendingDepositButton && (
              <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                <Button
                  main
                  isDepositPending
                  full
                  type="button"
                  onClick={() => setPendingDepositModalOpen(true)}>
                  {messages.pendingDeposit}
                </Button>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const MobileDocumentsAndRiskContent = () => {
    return (
      <>
        {/* DOCUMENTS BLOCK */}
        <Card className="flex-col bg-neutral-600 flex-shrink-0 order-3 rounded-xxl mt-4">
          <div className="flex flex-grow flex-col">
            <Heading level={2} className="font-semibold leading-9 lg:flex">
              {messages.documents}
            </Heading>
            <Markdown
              styles={{
                heading4: {
                  fontSize: 16,
                },
              }}>
              {messages.documentsBlurb}
            </Markdown>
            {factsheet && (
              <Button
                main
                secondary
                full
                className="mt-5"
                onClick={() => window.open(import.meta.env.VITE_ALTIFY_CDN_URL + factsheet, '_blank')}>
                <img
                  src={downloadIcon}
                  alt="Download Factsheet"
                  className="mr-2"
                />
                {getDisplayAssetCode(product?.code)} {messages.factsheet}
              </Button>
            )}
            {methodology && (
              <Button
                main
                secondary
                full
                className="mt-5"
                onClick={() => window.open(import.meta.env.VITE_ALTIFY_CDN_URL + methodology, '_blank')}>
                <img
                  src={downloadIcon}
                  alt="Download Crypto Bundle Methodology"
                  className="mr-2"
                />
                {messages.bundleMethodology}
              </Button>
            )}
          </div>
        </Card>

        {/* DISCLAIMER BLOCK */}
        <Card className="flex-col bg-neutral-600 flex-shrink-0 order-3 rounded-xxl mt-4">
          <div className="flex flex-grow flex-col">
            <Heading level={2} className="font-semibold leading-9 lg:flex mb-4">
              {messages.riskInformation}
            </Heading>
            <Markdown
              styles={{
                heading4: {
                  fontSize: 16,
                },
              }}>
              {messages.defaultRiskInformationBlurb}
            </Markdown>
          </div>
        </Card>
      </>
    );
  };

  const DocumentsRiskContent = () => {
    return (
      <>
        {/* DOCUMENTS BLOCK */}
        <Card
          className={`flex-col bg-neutral-600 hidden lg:flex flex-shrink-0 order-2 rounded-xxl`}>
          <div className="flex flex-grow flex-col">
            <Heading level={2} className="font-semibold leading-9 lg:flex">
              {messages.documents}
            </Heading>
            <Markdown
              styles={{
                heading4: {
                  fontSize: 16,
                },
              }}>
              {messages.documentsBlurb}
            </Markdown>
            {factsheet && (
              <Button
                main
                secondary
                full
                className="mt-5"
                onClick={() =>
                  window.open(
                    `${import.meta.env.VITE_ALTIFY_CDN_URL}${factsheet}`,
                    '_blank',
                  )
                }>
                <img
                  src={downloadIcon}
                  alt="Download Factsheet"
                  className="mr-2"
                />
                {getDisplayAssetCode(product?.code)} {messages.factsheet}
              </Button>
            )}
            {methodology && (
              <Button
                main
                secondary
                full
                className="mt-5"
                onClick={() =>
                  window.open(
                    `${import.meta.env.VITE_ALTIFY_CDN_URL}${methodology}`,
                    '_blank',
                  )
                }>
                <img
                  src={downloadIcon}
                  alt="Download Crypto Bundle Methodology"
                  className="mr-2"
                />
                {messages.bundleMethodology}
              </Button>
            )}
          </div>
        </Card>

        {/* DISCLAIMER BLOCK */}
        <Card className="flex-col bg-neutral-600 hidden lg:flex flex-shrink-0 order-3 rounded-xxl">
          <div className="flex flex-grow flex-col">
            <Heading level={2} className="font-semibold leading-9 lg:flex">
              {messages.riskInformation}
            </Heading>
            <Markdown
              styles={{
                heading4: {
                  fontSize: 16,
                },
              }}>
              {messages.defaultRiskInformationBlurb}
            </Markdown>
          </div>
        </Card>
      </>
    );
  };
  // #endregion END INLINE CONTENT RENDERERS

  const timeFrameTabs = Object.values(timeFrames).map(frame => ({
    value: frame.key,
    label: frame.name,
  }));

  const activeTimeFrameIndex = timeFrameTabs.findIndex(
    item => item.value === timeFrame,
  );

  // ADDITIONAL BUTTON STATES
  const showDepositButton =
    !product?.isBuyable &&
    product?.isDepositable &&
    !cryptoDepositProduct?.pendingOrderId;
  const showNotVerifiedButton = !isVerified && !isVerificationPending;
  const showNotVerificationPendingButton =
    isVerificationPending || isVerificationInProgress;
  const showPendingDepositButton =
    !product?.isBuyable &&
    product?.isDepositable &&
    cryptoDepositProduct?.pendingOrderId;

  // REF FOR TRACKING VISIBILITY
  const priceRef = useRef<HTMLDivElement>(null);
  const [visibilityPercentage, setVisibilityPercentage] = useState(0);
  // INTERSECTION OBSERVER FOR VISIBILITY TRACKING
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Calculate hidden percentage (inverse of visibility)
        const hiddenPercentage = Math.round(
          (1 - entry.intersectionRatio) * 100,
        );
        setVisibilityPercentage(hiddenPercentage);
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], // Multiple thresholds for smooth tracking
        rootMargin: '-80px 0px 0px 0px', // Account for sticky header
      },
    );

    if (priceRef.current) {
      observer.observe(priceRef.current);
    }

    return () => observer.disconnect();
  }, []);
  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 justify-between flex-wrap px-4 lg:px-6">
        {/* DESKTOP HOLDINGS COL VIEW */}
        {hasHoldings && (
          <Card className="flex-col bg-neutral-600 hidden lg:flex basis-[49.15%] flex-shrink-0 order-0 rounded-xxl">
            <div className="flex justify-between">
              <div className="flex flex-row">
                <div className="h-10 w-10 mr-4">
                  <CurrencyIcon
                    currencyCode={code?.toUpperCase()}
                    isLockedAsset={
                      product?.type == ActiveProductDtoTypeEnum.Locked
                    }
                  />
                </div>
                <div className="flex flex-col mb-4 -mt-1">
                  <Heading
                    level={2}
                    className="hidden font-semibold leading-9 lg:flex">
                    {product?.name}
                  </Heading>
                  <Typography size="medium" className="text-grey-light">
                    {getCurrencyTypeLabel(code, product?.type!, language)}
                  </Typography>
                </div>
              </div>
            </div>

            <DesktopHoldings />
            {/* MOBILE BUTTONS */}
            {<ButtonGroup />}
          </Card>
        )}

        {/* DESKTOP CONTENT COL VIEW */}
        <Card
          className={`flex flex-col bg-transparent basis-[49.15%] self-stretch visible ${hasHoldings ? 'order-2' : 'order-0'} px-0 py-0 lg:px-0 lg:py-0 gap-4`}>
          <Card
            className={`flex-col bg-neutral-600 hidden lg:flex flex-shrink-0 ${hasHoldings ? 'order-2' : 'order-0 basis-full'} rounded-xxl`}>
            {!hasHoldings ? (
              <div className="flex justify-between">
                <div className="flex flex-row">
                  <div className="h-10 w-10 mr-4">
                    <CurrencyIcon
                      currencyCode={code?.toUpperCase()}
                      isLockedAsset={
                        product?.type == ActiveProductDtoTypeEnum.Locked
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <Heading
                      level={2}
                      className="hidden font-semibold leading-9 lg:flex">
                      {product?.name}
                    </Heading>
                    <Typography size="medium" className="text-grey-light">
                      {getCurrencyTypeLabel(
                        product?.code!,
                        product?.type!,
                        language,
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                <div className="flex flex-row">
                  <Heading
                    level={2}
                    className="hidden font-semibold leading-9 lg:flex">
                    {messages.about} {product?.name}
                  </Heading>
                </div>
              </div>
            )}
            {/* DESKTOP "CONTENT" AND/OR HOLDINGS SECTION */}
            {product?.type == ActiveProductDtoTypeEnum.Locked ? (
              <LockedProductContent />
            ) : (
              <ProductContent />
            )}
            {/* MOBILE BUTTONS */}
            {!hasHoldings && <ButtonGroup />}
          </Card>

          {/* HOLDINGS DOCUMENTS AND RISK */}
          {hasHoldings && (
            <>
              <DocumentsRiskContent />
            </>
          )}
        </Card>

        {/* PERFORMANCE GRAPH */}
        <Card
          className={`bg-neutral-600 basis-[49.15%] flex-shrink-0 order-1 rounded-xxl w-full mt-[80px] lg:mt-0`}>
          {/* Heading && dropdown */}
          <div className="flex flex-row items-center justify-between">
            {/* Returns Heading */}
            <div className="flex flex-col justify-between items-start">
              <div
                ref={priceRef}
                className="flex whitespace-nowrap text-base font-semibold not-italic leading-7 tracking-wide">
                {product?.rate ? (
                  <RollingAmount
                    amount={formatCurrency(
                      product?.rate,
                      functionalCurrency?.code!,
                    )}
                    isPortfolioAmount={false}
                    className='text-5xl'
                  />
                ) : (
                  <div className="flex flex-row justify-between items-start text-header-text text-body-text lg:text-base whitespace-nowrap font-semibold lg:font-normal">
                    -
                  </div>
                )}
              </div>
              <div className="flex flex-row items-start gap-2 mt-2">
                <GainLoss value={assetGainPercentage} background />
                <Typography>{overTimePeriodMessages[timeFrame]}</Typography>
                <InfoButton
                  title={messages.performance}
                  text={messages.assetGainLossTooltip}
                  className='mt-1'
                />
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-between items-start mt-3">
            {/* Dropdown - time frames */}
            <Tabs
              underline={false}
              small={true}
              items={timeFrameTabs}
              activeIndex={activeTimeFrameIndex}
              onClickItem={key => setTimeframeKey(key.value)}
            />
          </div>
          {/* Performance graph */}
          <div className="flex flex-col">
            <ProductGraph
              isRoundIcon
              primaryColor={product?.colors?.primary}
              primaryData={graphData}
              productCode={code}
              timeFrame={timeFrame}
              lineWidth={2}
              fiatCode={functionalCurrency?.code!}
            />
          </div>
        </Card>

        {/* MOBILE HEADER CARD */}
        <Card className="bg-body-background w-full fixed lg:hidden px-4 py-4 flex flex-row order-0 justify-between top-8 left-0 h-[80px] z-40 w-full">
          <div className="flex flex-grow flex-row mx-3 items-center gap-2">
            <CurrencyIcon
              currencyCode={product?.code}
              isLockedAsset={product?.type == ActiveProductDtoTypeEnum.Locked}
            />
            <div className="flex flex-grow flex-col">
              <Heading
                level={4}
                className="lg:hidden font-semibold leading-1 lg:flex text-white">
                {product?.name}
              </Heading>
              <Typography size="small" className="text-grey-light mt-0">
                {getCurrencyTypeLabel(code, product?.type!, language)}
              </Typography>
            </div>
          </div>
          <div
            style={{
              opacity: visibilityPercentage / 100,
            }}
            className="flex mx-3 pt-2 items-end flex-grow flex-col justify-end">
            <RollingAmount
              amount={formatCurrency(product?.rate, functionalCurrency?.code!)}
              isPortfolioAmount={false}
            />
            <GainLoss value={assetGainPercentage} background />
          </div>
        </Card>

        {/* MOBILE CONTENT/TABS */}
        <Card className="flex flex-col bg-transparent basis-full self-stretch visible lg:hidden order-1  px-0 py-0">
          <CustomTabs floatingTabs={true} listOfElements={mobileTabs()} />
          {/* MOBILE BUTTONS */}
          {<ButtonGroup />}
        </Card>

        <Card
          className={`hidden lg:flex flex-col bg-neutral-600 lg:basis-[49.15%] flex-shrink-0 order-1 rounded-xxl`}>
            <div className="flex flex-col justify-center items-center lg:items-start">
              <Heading level={2} className="font-semibold leading-9 lg:flex text-center">
                {messages.breakdown}
              </Heading>
            </div>
          <ConstituentsPie noPadding bundle={activeBundle} />
        </Card>

        {/* NON HOLDINGS DOCUMENTS AND RISK */}
        {!hasHoldings && (
          <Card
            className={`flex flex-col bg-transparent basis-[49.15%] self-stretch visible order-3 px-0 py-0 lg:px-0 lg:py-0 gap-4`}>
            <DocumentsRiskContent />
          </Card>
        )}
      </div>
      {/* wired */}
      <DisclaimerModal
        messages={messages}
        content={
          <div className="flex flex-col items-center gap-6">
            <p className="text-start">
              Returns are unaudited and are calculated and displayed after
              deducting all fees and expenses. Remember that the performance of
              an investment will be reduced by all applicable fees and that your
              returns may vary based on the timing of entry into the investment.
            </p>

            <p className="text-start">
              Actual returns may differ materially from historic returns and an
              investor may lose all or a substantial part of your investment in
              the Bundle. Any investment displayed here is intended to be used
              for informational purposes only and should not be relied upon as
              the sole basis for making any investment decision. Please note
              that the performance and information shared here is not intended
              to be a recommendation or advice to make an investment. You should
              consider an investment based on your investment time horizon
              and/or risk tolerance.
            </p>
            <p className="text-start">
              {messages.graphSubText1}{' '}
              <a
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
                href={`${getMarketingUrl()}/fees`}>
                {messages.graphSubText2}
              </a>{' '}
              {messages.graphSubText3}{' '}
              <a
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
                href={`${getMarketingUrl()}/legal/risk-disclosure`}>
                {messages.graphSubText4}
              </a>{' '}
              {messages.graphSubText5}{' '}
              <a
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
                href={`${getMarketingUrl()}/legal/risk-disclosure`}>
                {messages.graphSubText6}
              </a>
              .
            </p>
          </div>
        }
        isOpen={disclaimerModalOpen}
        hide={() => setDisclaimerModalOpen(false)}
      />
      <ReturnsModal
        messages={messages}
        isOpen={returnsModalOpen}
        hide={() => setReturnsModalOpen(false)}
      />
      <VerifyModal
        verificationPendingMessages={verificationPendingMessages}
        sub
        messages={messages}
        hide={() => setVerifyModal(false)}
        isOpen={verifyModal}
      />
    </>
  );
}

export default ProductBundleData;
